import { useScenarioForm } from './contexts';
import {
  DropdownField,
  NumberField,
  TanstackFieldWrapper,
} from 'components/form-fields-2';
import { Button } from '@carbon/react';
import { Add, TrashCan } from '@carbon/react/icons';
import { capitalize } from 'lodash-es';
import {
  ScalingConfigRow,
  ScalingConfigResponse,
  ScalingConfigsResponse,
  ScalingMethod,
  ScalingVariable,
} from './types';
import { defaultScalingConfig } from './default-values';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import sectionStyles from './config-sections.module.scss';
import styles from './scaling-normalization-configs.module.scss';
import { useEffect, useMemo } from 'react';
import { ScalingConfigsSkeleton } from './section-skeletons';
import { useConfigSwitch } from './hooks';
import { atom, useAtomValue } from 'jotai';
import { createResourceAtom } from 'lib/atoms';

const FALLBACK_ATOM = atom(null);

const SCALING_VARIABLES: ScalingVariable[] = [
  'perforated_lateral_length_ft',
  'proppant_intensity_lbs_ft',
  'fluid_intensity_bbls_ft',
  'horizontal_well_spacing_in_zone_ft',
  'horizontal_well_spacing_in_all_zones_ft',
  'true_distance_well_spacing_ft',
  'pay_thickness_ft',
];

const SCALING_VARIABLE_LABELS: Record<ScalingVariable, string> = {
  perforated_lateral_length_ft: 'Perforated Lateral Length (ft)',
  proppant_intensity_lbs_ft: 'Proppant Intensity (lbs/ft)',
  fluid_intensity_bbls_ft: 'Fluid Intensity (bbls/ft)',
  horizontal_well_spacing_in_zone_ft: 'Horizontal Well Spacing In Zone (ft)',
  horizontal_well_spacing_in_all_zones_ft:
    'Horizontal Well Spacing In All Zones (ft)',
  true_distance_well_spacing_ft: 'True Distance Well Spacing (ft)',
  pay_thickness_ft: 'Pay Thickness (ft)',
};

const SCALING_METHODS: ScalingMethod[] = ['linear', 'square'];

const scalingVariableLabel = (value: string | null) => {
  if (!value) return '';
  return (
    SCALING_VARIABLE_LABELS[value as ScalingVariable] ??
    value
      .split('_')
      .map((word) => capitalize(word))
      .join(' ')
  );
};

const mapResponseToScalingConfig = (
  resp: ScalingConfigResponse
): ScalingConfigRow => ({
  id: String(resp.id),
  scalingVariable: resp.scalingVariable as ScalingVariable,
  scalingMethod: (resp.scalingMethod as ScalingMethod) || 'linear',
  degradationFactorPercent:
    resp.degradationFactor !== null ? parseFloat(resp.degradationFactor) : 0,
  degradationBase:
    resp.degradationBaseFt !== null ? parseFloat(resp.degradationBaseFt) : 0,
});

export const ScalingNormalizationConfigs = () => {
  const { form, selectedAreaConfig, setFieldBaseline, readOnly } =
    useScenarioForm();

  // Fetch existing scaling configs from API
  const scalingConfigsHref =
    selectedAreaConfig?.resources?.scalingConfigs?.href || null;

  const scalingConfigsAtom = useMemo(() => {
    if (!scalingConfigsHref) return FALLBACK_ATOM;
    return createResourceAtom<ScalingConfigsResponse>(scalingConfigsHref);
  }, [scalingConfigsHref]);

  const scalingConfigsData = useAtomValue(scalingConfigsAtom);

  const initializedForConfigRef = useConfigSwitch(selectedAreaConfig);

  // When API data loads, override with fetched values
  useEffect(() => {
    const configId = selectedAreaConfig?.id ?? null;
    if (initializedForConfigRef.current === configId) return;
    if (!scalingConfigsData?.data || !('results' in scalingConfigsData.data))
      return;
    initializedForConfigRef.current = configId;
    const results = scalingConfigsData.data.results;
    const configs = results.map(mapResponseToScalingConfig);
    form.setFieldValue('scalingConfigs', configs);
    setFieldBaseline('scalingConfigs', configs);
  }, [
    scalingConfigsData,
    form,
    setFieldBaseline,
    selectedAreaConfig?.id,
    initializedForConfigRef,
  ]);

  return (
    <Accordion defaultExpanded>
      <AccordionSummary className={sectionStyles.accordionSummaryBorder}>
        <h3 className={sectionStyles.sectionTitle}>
          Scaling & Normalization Configs
        </h3>
      </AccordionSummary>
      <AccordionDetails>
        <div className={sectionStyles.sectionDetails}>
          <p className={styles.description}>
            Configure scaling and normalization parameters for type curve
            generation.
          </p>

          {scalingConfigsData?.loading ? (
            <ScalingConfigsSkeleton />
          ) : (
            <form.Subscribe selector={(state) => state.values.scalingConfigs}>
              {(scalingConfigs) => {
                const usedVariables = new Set(
                  scalingConfigs.map((c) => c.scalingVariable)
                );
                const allOptionsUsed =
                  scalingConfigs.length >= SCALING_VARIABLES.length;

                return (
                  <>
                    {scalingConfigs.map((config, index) => (
                      <div
                        key={config.id || index}
                        className={styles.configRow}
                      >
                        <form.Field
                          name={
                            `scalingConfigs[${index}].scalingVariable` as any
                          }
                        >
                          {(field: any) => (
                            <TanstackFieldWrapper<ScalingVariable>
                              field={field}
                            >
                              {(props) => (
                                <DropdownField
                                  id={`scalingVariable-${index}`}
                                  label="Select variable"
                                  titleText="Scaling Variable"
                                  items={SCALING_VARIABLES.filter(
                                    (v) =>
                                      !usedVariables.has(v) ||
                                      v === config.scalingVariable
                                  )}
                                  itemToString={(item: string | null) =>
                                    scalingVariableLabel(item)
                                  }
                                  disabled={readOnly}
                                  {...props}
                                />
                              )}
                            </TanstackFieldWrapper>
                          )}
                        </form.Field>

                        <form.Field
                          name={`scalingConfigs[${index}].scalingMethod` as any}
                        >
                          {(field: any) => (
                            <TanstackFieldWrapper<ScalingMethod> field={field}>
                              {(props) => (
                                <DropdownField
                                  id={`scalingMethod-${index}`}
                                  label="Select method"
                                  titleText="Scaling Method"
                                  items={SCALING_METHODS}
                                  itemToString={(item: string | null) =>
                                    capitalize(item ?? '')
                                  }
                                  disabled={readOnly}
                                  {...props}
                                />
                              )}
                            </TanstackFieldWrapper>
                          )}
                        </form.Field>

                        <form.Field
                          name={
                            `scalingConfigs[${index}].degradationFactorPercent` as any
                          }
                        >
                          {(field: any) => (
                            <TanstackFieldWrapper<number | null> field={field}>
                              {(props) => (
                                <NumberField
                                  id={`degradationFactor-${index}`}
                                  label="Degradation Factor (%)"
                                  disabled={readOnly}
                                  isAllowed={(v) => v >= 0 && v <= 100}
                                  {...props}
                                />
                              )}
                            </TanstackFieldWrapper>
                          )}
                        </form.Field>

                        <form.Field
                          name={
                            `scalingConfigs[${index}].degradationBase` as any
                          }
                        >
                          {(field: any) => (
                            <TanstackFieldWrapper<number | null> field={field}>
                              {(props) => (
                                <NumberField
                                  id={`degradationBase-${index}`}
                                  label="Degradation Base (ft)"
                                  disabled={readOnly}
                                  {...props}
                                />
                              )}
                            </TanstackFieldWrapper>
                          )}
                        </form.Field>

                        {!readOnly && (
                          <div className={styles.deleteCell}>
                            <Button
                              kind="ghost"
                              size="sm"
                              hasIconOnly
                              renderIcon={TrashCan}
                              iconDescription="Remove"
                              onClick={() => {
                                const current = [...scalingConfigs];
                                current.splice(index, 1);
                                form.setFieldValue('scalingConfigs', current);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    {!readOnly && (
                      <Button
                        kind="tertiary"
                        size="sm"
                        renderIcon={Add}
                        className={styles.addButton}
                        disabled={allOptionsUsed}
                        onClick={() => {
                          const firstAvailable = SCALING_VARIABLES.find(
                            (v) => !usedVariables.has(v)
                          );
                          form.setFieldValue('scalingConfigs', [
                            ...scalingConfigs,
                            {
                              ...defaultScalingConfig,
                              scalingVariable:
                                firstAvailable ??
                                defaultScalingConfig.scalingVariable,
                            },
                          ]);
                        }}
                      >
                        Add Scaling Variable
                      </Button>
                    )}
                  </>
                );
              }}
            </form.Subscribe>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
